/**
 * 微信 / iOS WebView 静音自动播放解锁与重试
 *
 * 要点：
 * 1. play() 必须在用户手势的「同步」调用栈内，await 会丢掉授权。
 * 2. 微信里「滑动/滚动」通常不算媒体手势，只有点击/轻触才算 —— 所以光滑可能播不了。
 * 3. 微信可用 WeixinJSBridge.invoke('getNetworkType') 回调解锁自动播（经典做法）。
 */

export function isWeChatUA(): boolean {
  if (typeof navigator === 'undefined') return false
  return /MicroMessenger/i.test(navigator.userAgent)
}

type WeixinBridge = {
  invoke: (method: string, params: object, callback?: () => void) => void
}

function getWeixinBridge(): WeixinBridge | null {
  try {
    return (window as unknown as { WeixinJSBridge?: WeixinBridge }).WeixinJSBridge ?? null
  } catch {
    return null
  }
}

function prepareMuted(video: HTMLVideoElement): void {
  video.muted = true
  video.defaultMuted = true
  video.setAttribute('muted', '')
  video.playsInline = true
}

/** 同步发起 play（不 await），用于手势 / Bridge 回调 */
function playMutedSync(video: HTMLVideoElement): void {
  if (!video.isConnected) return
  if (!video.src && !video.currentSrc) return
  prepareMuted(video)
  try {
    const result = video.play()
    if (result && typeof result.catch === 'function') {
      result.catch(() => undefined)
    }
  } catch {
    // ignore
  }
}

/** 极短静音 wav，用于在 touch 时抢先解锁媒体上下文 */
const SILENT_WAV =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA'

let silentAudio: HTMLAudioElement | null = null

function playSilentUnlockSync(): void {
  try {
    if (!silentAudio) {
      silentAudio = new Audio(SILENT_WAV)
      silentAudio.preload = 'auto'
      silentAudio.loop = false
      silentAudio.volume = 0
    }
    silentAudio.currentTime = 0
    const result = silentAudio.play()
    if (result && typeof result.catch === 'function') {
      result.catch(() => undefined)
    }
  } catch {
    // ignore
  }

  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (AC) {
      const ctx = new AC()
      void ctx.resume()
      const buffer = ctx.createBuffer(1, 1, 22050)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(ctx.destination)
      source.start(0)
    }
  } catch {
    // ignore
  }
}

type PendingEntry = {
  video: HTMLVideoElement
  active: boolean
}

const pending = new Set<PendingEntry>()
let unlockBound = false
let mediaUnlocked = false

/** 同步冲刷挂起队列 */
export function flushMutedAutoplaySync(): void {
  mediaUnlocked = true
  for (const entry of [...pending]) {
    if (!entry.active) {
      pending.delete(entry)
      continue
    }
    const video = entry.video
    if (!video.isConnected) {
      pending.delete(entry)
      continue
    }
    if (!video.paused && !video.ended) {
      pending.delete(entry)
      continue
    }
    if (!video.src && !video.currentSrc) continue

    playMutedSync(video)
    window.setTimeout(() => {
      if (!entry.active) return
      if (!video.paused) pending.delete(entry)
    }, 0)
  }
}

/**
 * 微信 Bridge 解锁：在 getNetworkType 回调里 play，不依赖点击。
 * 滑动时浏览器不给媒体手势，但 Bridge 回调可以播。
 */
function unlockViaWeixinBridge(): void {
  const bridge = getWeixinBridge()
  if (!bridge?.invoke) {
    flushMutedAutoplaySync()
    return
  }
  try {
    bridge.invoke('getNetworkType', {}, () => {
      playSilentUnlockSync()
      flushMutedAutoplaySync()
      // 再补两枪，等懒加载刚写上 src 的视频
      window.setTimeout(() => flushMutedAutoplaySync(), 100)
      window.setTimeout(() => flushMutedAutoplaySync(), 400)
    })
  } catch {
    flushMutedAutoplaySync()
  }
}

/** 手势路径：静音音频 + 视频（点击有效；纯滑动在微信里常无效） */
function unlockViaUserGesture(): void {
  playSilentUnlockSync()
  flushMutedAutoplaySync()
}

function bindUnlockListeners(): void {
  if (unlockBound || typeof window === 'undefined') return
  unlockBound = true

  const onBridgeReady = () => unlockViaWeixinBridge()
  document.addEventListener('WeixinJSBridgeReady', onBridgeReady, false)
  if (getWeixinBridge()) unlockViaWeixinBridge()

  // 点击 / 触摸结束：有效用户手势
  const onTapGesture = () => unlockViaUserGesture()
  document.addEventListener('click', onTapGesture, { capture: true, passive: true })
  document.addEventListener('touchend', onTapGesture, { capture: true, passive: true })

  // touchstart：同步抢播；若这次触摸随后变成「滑动」，微信仍可能拒绝 video.play
  // 所以同时走 Bridge 解锁，滑动场景靠 Bridge
  const onTouchStart = () => {
    unlockViaUserGesture()
    if (isWeChatUA()) unlockViaWeixinBridge()
  }
  document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true })

  // 滑动本身不是媒体手势 → 用 Bridge 解锁，而不是直接指望 scroll 里的 play
  let scrollUnlockTimer = 0
  const onScroll = () => {
    if (!isWeChatUA()) {
      // 非微信：滚动时也试一下（部分安卓浏览器允许）
      flushMutedAutoplaySync()
      return
    }
    if (scrollUnlockTimer) return
    scrollUnlockTimer = window.setTimeout(() => {
      scrollUnlockTimer = 0
      unlockViaWeixinBridge()
    }, 50)
  }
  document.addEventListener('scroll', onScroll, { capture: true, passive: true })

  // 桌面
  document.addEventListener('mousemove', unlockViaUserGesture, {
    capture: true,
    passive: true,
    once: true
  })
}

/** 尽早挂上手势 / Bridge 监听 */
export function ensureMutedAutoplayUnlock(): void {
  bindUnlockListeners()
  if (isWeChatUA()) unlockViaWeixinBridge()
}

/**
 * 登记需要静音自动播的视频；失败则挂起等待解锁。
 */
export function requestMutedAutoplay(video: HTMLVideoElement | null | undefined): () => void {
  if (!video) return () => undefined

  const entry: PendingEntry = { video, active: true }
  pending.add(entry)
  bindUnlockListeners()

  const retryWhenReady = () => {
    if (!entry.active || !video.paused) return
    playMutedSync(video)
    if (isWeChatUA()) unlockViaWeixinBridge()
  }
  video.addEventListener('loadedmetadata', retryWhenReady)
  video.addEventListener('canplay', retryWhenReady)

  prepareMuted(video)
  playMutedSync(video)

  // 视频通常是滚动到能力区时才获得 src，早期的 BridgeReady
  // 回调很可能已经结束。入队后立即再走一次 Bridge，避免只能等点击。
  if (isWeChatUA()) unlockViaWeixinBridge()

  const retryTimer = window.setTimeout(() => {
    if (!entry.active) return
    if (!video.paused) {
      pending.delete(entry)
      return
    }
    if (mediaUnlocked) flushMutedAutoplaySync()
    if (isWeChatUA()) unlockViaWeixinBridge()
  }, 200)

  return () => {
    entry.active = false
    pending.delete(entry)
    window.clearTimeout(retryTimer)
    video.removeEventListener('loadedmetadata', retryWhenReady)
    video.removeEventListener('canplay', retryWhenReady)
  }
}

/** 对一组视频同步静音播放 */
export function playAllMutedSync(videos: Array<HTMLVideoElement | null | undefined>): void {
  mediaUnlocked = true
  playSilentUnlockSync()
  videos.forEach((video) => {
    if (!video) return
    playMutedSync(video)
  })
  flushMutedAutoplaySync()
  if (isWeChatUA()) unlockViaWeixinBridge()
}
